import { useMemo } from 'react';
import { useUser } from '@/shared/hooks/useUser';
import type { FeatureFlag } from '@/features/featureFlags';
import { useSandboxMode } from '@/features/sandbox';
import { useFeatureFlags } from '@/shared/featureFlags';

export interface FeatureAccessResult {
  /** Render the surface at all? */
  shouldShow: boolean;
  /** If rendered, is it interactive (vs. greyed out)? */
  isEnabled: boolean;
  /** Convenience flag for "in_development" — drives greyed/dev-badge UI. */
  isInDevelopment: boolean;
}

/**
 * Conservative fallback applied while flags are loading or for keys the
 * server doesn't know about. We hide entirely so users never see a feature
 * surface flicker on, then off, when the response arrives. Adjust at the
 * call site if a feature wants a different fallback.
 */
const HIDDEN_FALLBACK: FeatureAccessResult = {
  shouldShow: false,
  isEnabled: false,
  isInDevelopment: false,
};

/**
 * Hook to check feature access based on the server-defined ``Feature`` row
 * for ``feature.id`` plus the current user's role.
 *
 * Returns:
 * - shouldShow: render the surface at all
 * - isEnabled: clickable / functional
 * - isInDevelopment: visible but greyed for non-staff/non-admin
 *
 * Behavior summary:
 *   - "all"            → shown + enabled for everyone
 *   - "admin"          → shown + enabled for superusers only
 *   - "staff"          → shown + enabled for staff/superusers only
 *   - "in_development" → shown to everyone; enabled for staff/admin/sandbox
 *                        users or clients with ``beta_features_enabled``
 *
 * Special cases:
 *   - ``Feature.is_enabled === false`` acts as a global kill-switch:
 *     ``shouldShow = false``, regardless of role. Useful for ops to disable
 *     a feature without a redeploy.
 *   - While the flag map is still loading, or if the server doesn't return
 *     this ``flag_key`` at all, we fall back to ``HIDDEN_FALLBACK``.
 *
 * Sandbox interaction:
 *   When the user is in sandbox mode, in_development features are treated
 *   as enabled regardless of their client's beta-features flag — the
 *   sandbox is intentionally a place to test beta functionality.
 */
export const useFeatureAccess = (feature: FeatureFlag): FeatureAccessResult => {
  const { user } = useUser();
  const { inSandboxMode } = useSandboxMode();
  const { byKey, isLoading } = useFeatureFlags();

  return useMemo(() => {
    if (isLoading) return HIDDEN_FALLBACK;

    const def = byKey[feature.id];
    if (!def) return HIDDEN_FALLBACK;

    // Global kill-switch — ops can disable a feature server-side and the
    // frontend honors it without a redeploy.
    if (!def.is_enabled) {
      return { shouldShow: false, isEnabled: false, isInDevelopment: false };
    }

    const isAdmin = user?.is_superuser === true;
    const isStaff = user?.is_staff === true;
    const hasClientBetaFeatures = user?.client_info?.beta_features_enabled === true;

    switch (def.access_level) {
      case 'all':
        return { shouldShow: true, isEnabled: true, isInDevelopment: false };

      case 'admin':
        return { shouldShow: isAdmin, isEnabled: isAdmin, isInDevelopment: false };

      case 'staff':
        return { shouldShow: isStaff, isEnabled: isStaff, isInDevelopment: false };

      case 'in_development':
        return {
          shouldShow: true,
          isEnabled: isStaff || isAdmin || hasClientBetaFeatures || inSandboxMode,
          isInDevelopment: true,
        };

      default:
        return HIDDEN_FALLBACK;
    }
  }, [feature.id, byKey, isLoading, user, inSandboxMode]);
};
